import { Button, Popconfirm, Space, Switch, Tooltip } from "antd";
import { ColumnType } from "antd/es/table";
import { DataIndex, DataType } from "../utils/types";
import { IoMdMail } from "react-icons/io";
import { MdLockReset } from "react-icons/md";

export enum LicenseType {
  UCaaSEnterprise = 'UCaaS Enterprise',
  UCaaSBusiness = 'UCaaS Business',
  MeetingLicense = 'Meeting License',
  VirtualOffice = 'Virtual Office',
  AIUsers = 'AI Users',
}

export const getColumns = (
  getColumnSearchProps: (dataIndex: DataIndex) => any,
  licenseType: string,
  handleToggle: (id: string, userSMSEnabled: boolean) => void,
  handleChangeSMS: (id: string, userSMSEnabled: boolean) => void,
  handleNotify: (id: string) => void,
  loadingNotify: { [key: string]: boolean },
  handleResetPassword: (id: string) => void,
  loadingResetPassword: { [key: string]: boolean },
  userLicenses: DataType[],
  options?: { readOnly?: boolean }
): ColumnType<any>[] => {
  const readOnly = options?.readOnly ?? false;
  const baseColumns: ColumnType<any>[] = [
    {
      title: "User ID",
      dataIndex: "id",
      key: "id",
      align: "center" as const,
      responsive: ["md"],
      ...getColumnSearchProps("id"),
    },
    {
      title: "Name",
      dataIndex: "loginName",
      key: "loginName",
      align: "center" as const,
      ...getColumnSearchProps("loginName"),
      render: (_: any, record: DataType) => `${record.loginName}`,
    },
    {
      title: "User Group",
      dataIndex: "userGroupName",
      key: "userGroupName",
      align: "center" as const,
      responsive: ["md"],
      ...getColumnSearchProps("userGroupName"),
    },
    {
      title: readOnly
        ? "Status"
        : licenseType === LicenseType.UCaaSEnterprise
        ? "UCaaS"
        : "Activate License",
      dataIndex: "activateLicense",
      key: "activateLicense",
      align: "center" as const,
      render: (_: any, record: DataType) =>
        readOnly ? (
          <span>{record.customerLicenseId ? "Active" : "Inactive"}</span>
        ) : (
          <Switch
            checked={!!record.customerLicenseId}
            onChange={() => handleToggle(record.id, record.userSMSEnabled)}
          />
        ),
    },
  ];

  if (!readOnly && licenseType === LicenseType.UCaaSEnterprise) {
    baseColumns.push({
      title: "SMS",
      dataIndex: "userSMSEnabled",
      key: "userSMSEnabled",
      align: "center" as const,
      render: (_, record) => (
        <Switch
          checked={!!record.userSMSEnabled}
          onChange={() => handleChangeSMS(record.id, record.userSMSEnabled)}
        />
      ),
    });
  }

  const hasActivatedLicenses = userLicenses.some(
    (user) => user.customerLicenseId
  );

  if (hasActivatedLicenses && licenseType.includes("UCaaS") && !readOnly) {
    baseColumns.push({
      title: "",
      key: "actions",
      align: "center" as const,
      width: "10%",
      render: (_: any, record: DataType) =>
        record.customerLicenseId ? (
          <Space direction="vertical" size="small">
            <Tooltip title="Send credentials">
              <Button
                type="primary"
                icon={<IoMdMail />}
                aria-label="Send credentials"
                onClick={() => handleNotify(record.id)}
                loading={loadingNotify[record.id]}
              />
            </Tooltip>
            <Popconfirm
              title="Reset password?"
              description="This will reset the user's password."
              okText="Reset"
              cancelText="Cancel"
              placement="left"
              onConfirm={() => handleResetPassword(record.id)}
            >
              <Tooltip title="Reset password">
                <Button
                  type="primary"
                  icon={<MdLockReset />}
                  aria-label="Reset password"
                  loading={loadingResetPassword[record.id]}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ) : null,
    });
  }

  return baseColumns;
};


